/**
 * DrManiOptionCard - selectable option card for the DMM quiz.
 *
 * Uses DMM-native classes (dmm-option-*) from DrManiQuiz.css so the quiz keeps
 * its own naming convention, independent of the USCN option-card classes.
 * Reads the DMM spec shape (label + sublabel).
 *
 * The option emoji comes from DMM_ICON_EMOJI, keyed by the spec's `icon` value,
 * and sits above the label the way the USCN quiz renders its emoji.
 */
import React from 'react';
import type { QuestionOption } from '../../data/drManiQuiz/types';
import { dmmOptionEmoji } from '../../data/drManiQuiz/iconEmoji';

interface DrManiOptionCardProps {
  option: QuestionOption;
  isSelected: boolean;
  isMultiple: boolean;
  /** Pass the clicked card so botanical particles launch from the action. */
  onSelect: (source: HTMLButtonElement) => void;
}

const DrManiOptionCard: React.FC<DrManiOptionCardProps> = ({ option, isSelected, isMultiple, onSelect }) => {
  return (
    <button
      type="button"
      className={`dmm-option-card ${isSelected ? 'dmm-option-card-selected' : ''}`}
      onClick={(event) => onSelect(event.currentTarget)}
      aria-pressed={isSelected}
    >
      <div className="dmm-option-indicator">
        {isMultiple ? (
          <div className={`dmm-option-checkbox ${isSelected ? 'dmm-option-checkbox-checked' : ''}`}>
            {isSelected && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        ) : (
          <div className={`dmm-option-radio ${isSelected ? 'dmm-option-radio-checked' : ''}`}>
            {isSelected && <div className="dmm-option-radio-inner" />}
          </div>
        )}
      </div>

      <div className="dmm-option-card-content">
        {/* Emoji keyed off the option's icon value; unmapped keys fall back to the seedling. */}
        <span className="dmm-option-card-emoji" aria-hidden="true">
          {dmmOptionEmoji(option.icon)}
        </span>
        <div className="dmm-option-card-info">
          <span className="dmm-option-card-label">{option.label}</span>
          {option.sublabel && <span className="dmm-option-card-description">{option.sublabel}</span>}
        </div>
      </div>
    </button>
  );
};

export default DrManiOptionCard;
